import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import {
    FileText,
    Download,
    Eye,
    ClipboardList,
    Calendar,
    Search
} from 'lucide-react'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'

import { useAttachments, useServeFile, useAssessments } from '@/features/assessments/hooks'
import { useOrganizations } from '@/features/organizations/hooks'
import { useEngineQuestions } from '@/features/questions/hooks'
import { FilePreviewDialog } from '@/features/assessments/components/file-preview-dialog'
import { format } from 'date-fns'
import { useListState } from '@/hooks/use-list-state'
import { DataToolbar } from '@/components/ui/data-toolbar'
import { DataPagination } from '@/components/ui/data-pagination'
import { VirtualizedCombobox } from '@/components/ui/virtualized-combobox'

export default function AttachmentsPage() {
    // Initialize list state with URL params
    const listState = useListState({
        defaultPerPage: 10,
        defaultSortBy: 'created_at',
        defaultSortOrder: 'desc'
    })

    const [previewOpen, setPreviewOpen] = useState(false)
    const [selectedAttachment, setSelectedAttachment] = useState<any>(null)

    // Data Queries
    const { data: orgsRes } = useOrganizations({ per_page: 200 })
    const { data: questionsRes } = useEngineQuestions({ per_page: 1000 })

    // Get org_id from filters for assessment filtering
    const selectedOrgId = listState.filters.org_id as string | undefined
    const { data: assessmentsRes } = useAssessments(selectedOrgId ? { organization_id: selectedOrgId } as any : undefined)

    const { data: attachmentsRes, isLoading } = useAttachments({
        page: listState.page,
        per_page: listState.perPage,
        org_id: selectedOrgId,
        assessment_id: listState.filters.assessment_id as string | undefined,
        question_id: listState.filters.question_id as string | undefined,
        search: listState.search
    })

    const { mutate: downloadFile } = useServeFile()

    const attachments = attachmentsRes?.items || []
    const totalItems = attachmentsRes?.total || 0
    const totalPages = attachmentsRes?.pages || 1

    const organizationOptions = useMemo(() =>
        (orgsRes as any)?.items?.map((org: any) => ({ label: org.name, value: org.id })) || []
        , [orgsRes])

    const assessmentOptions = useMemo(() =>
        (assessmentsRes as any)?.items?.map((a: any) => ({ label: a.name, value: a.id })) || []
        , [assessmentsRes])

    const questionOptions = useMemo(() =>
        (questionsRes as any)?.items?.map((q: any) => ({ label: q.text, value: q.id })) || []
        , [questionsRes])

    const handlePreview = (attachment: any) => {
        setSelectedAttachment(attachment)
        setPreviewOpen(true)
    }

    const handleDownload = (attachment: any) => {
        downloadFile({
            assessmentId: attachment.assessment_id,
            questionId: attachment.question_id
        })
    }

    if (isLoading && listState.page === 1) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="lg" />
            </div>
        )
    }

    return (
        <motion.div
            className="space-y-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
        >
            {/* Header */}
            <div className="md:flex md:items-center md:justify-between">
                <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
                        Attachments
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                        View and manage all supporting documentation across assessments
                    </p>
                </div>
            </div>

            {/* Toolbar */}
            <DataToolbar
                listState={listState}
                searchPlaceholder="Search evidence..."
                sortOptions={[
                    { label: 'Date Uploaded', value: 'created_at' },
                    { label: 'Filename', value: 'attachment_name' },
                ]}
                filters={[
                    {
                        key: 'org_id',
                        label: 'Organization',
                        options: organizationOptions
                    },
                    {
                        key: 'assessment_id',
                        label: 'Assessment',
                        options: assessmentOptions
                    },
                ]}
                totalItems={totalItems}
                perPageOptions={[10, 25, 50]}
                extraFilters={
                    <div className="flex items-center gap-2 text-sm flex-shrink-0">
                        <div className="w-[320px]">
                            <VirtualizedCombobox
                                options={questionOptions}
                                value={(listState.filters.question_id as string) || ''}
                                onValueChange={(val) => listState.setFilter('question_id', val || undefined)}
                                placeholder="Filter by Question..."
                                searchPlaceholder="Search questions..."
                            />
                        </div>
                    </div>
                }
            />

            {/* Table */}
            <Card className="border shadow-none overflow-hidden">
                <CardContent className="p-0">
                    <Table>
                        <TableHeader className="bg-gray-50/50">
                            <TableRow>
                                <TableHead className="py-3 px-4 font-semibold text-gray-900">Evidence Name</TableHead>
                                <TableHead className="py-3 px-4 font-semibold text-gray-900">Question</TableHead>
                                <TableHead className="py-3 px-4 font-semibold text-gray-900">Organization</TableHead>
                                <TableHead className="py-3 px-4 font-semibold text-gray-900">Assessment</TableHead>
                                <TableHead className="py-3 px-4 font-semibold text-gray-900">Date Uploaded</TableHead>
                                <TableHead className="py-3 px-4 font-semibold text-gray-900 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {attachments.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="h-48 text-center text-muted-foreground">
                                        <div className="flex flex-col items-center gap-2">
                                            <FileText className="h-10 w-10 opacity-20" />
                                            <p>No attachments found matching your filters.</p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                attachments.map((att: any) => (
                                    <TableRow
                                        key={att.id}
                                        className="hover:bg-slate-50 transition-colors group"
                                    >
                                        <TableCell className="py-3 px-4">
                                            <div className="flex items-center gap-2">
                                                <FileText className="h-4 w-4 text-blue-500" />
                                                <span className="font-medium text-gray-900 truncate max-w-[200px]" title={att.filename}>
                                                    {att.filename}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="py-3 px-4 max-w-[300px]">
                                            <div className="flex items-start gap-2 text-sm text-gray-600">
                                                <Search className="h-3.5 w-3.5 mt-0.5 opacity-40 shrink-0" />
                                                <span className="line-clamp-2" title={att.question_text}>
                                                    {att.question_text}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="py-3 px-4 text-gray-600">
                                            {att.organization_name}
                                        </TableCell>
                                        <TableCell className="py-3 px-4">
                                            <div className="flex items-center gap-2 text-gray-600">
                                                <ClipboardList className="h-3.5 w-3.5 opacity-60" />
                                                <span className="truncate max-w-[150px]">{att.assessment_name}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="py-3 px-4 text-gray-500 text-sm">
                                            <div className="flex items-center gap-2">
                                                <Calendar className="h-3.5 w-3.5 opacity-60" />
                                                {att.created_at ? format(new Date(att.created_at), 'MMM dd, yyyy') : 'N/A'}
                                            </div>
                                        </TableCell>
                                        <TableCell className="py-3 px-4 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handlePreview(att)}
                                                    className="h-8 w-8 p-0"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDownload(att)}
                                                    className="h-8 w-8 p-0"
                                                >
                                                    <Download className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Pagination */}
            <DataPagination
                page={listState.page}
                totalPages={totalPages}
                onPageChange={listState.setPage}
                totalItems={totalItems}
                perPage={listState.perPage}
            />

            {selectedAttachment && (
                <FilePreviewDialog
                    open={previewOpen}
                    onOpenChange={setPreviewOpen}
                    assessmentId={selectedAttachment.assessment_id}
                    questionId={selectedAttachment.question_id}
                    filename={selectedAttachment.filename}
                />
            )}
        </motion.div>
    )
}
